import { ChangeDetectionStrategy, Component, computed, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, firstValueFrom, lastValueFrom, map, switchMap } from "rxjs";

import { UserDecryptionOptionsServiceAbstraction } from "@bitwarden/auth/common";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { OrganizationService } from "@bitwarden/common/admin-console/abstractions/organization/organization.service.abstraction";
import { Account, AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { AvatarService } from "@bitwarden/common/auth/abstractions/avatar.service";
import { ProfileResponse } from "@bitwarden/common/models/response/profile.response";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { UserPublicKey } from "@bitwarden/common/types/key";
import { DialogService } from "@bitwarden/components";
import { KeyService } from "@bitwarden/key-management";

import { DynamicAvatarComponent } from "../../../components/dynamic-avatar.component";
import { SharedModule } from "../../../shared";

import { ChangeAvatarDialogComponent } from "./change-avatar-dialog.component";
import { EditEmailDialogComponent } from "./edit-email-dialog.component";
import { EditNameDialogComponent } from "./edit-name-dialog.component";

@Component({
  selector: "app-profile",
  templateUrl: "profile.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, DynamicAvatarComponent],
})
export class ProfileComponent implements OnInit {
  readonly loading = signal(true);
  readonly profile = signal<ProfileResponse | undefined>(undefined);
  readonly fingerprint = signal<string | undefined>(undefined);
  readonly displayName = signal("");

  readonly userId = toSignal(
    this.accountService.activeAccount$.pipe(map((account) => account?.id ?? "")),
  );

  readonly email = toSignal(
    this.accountService.activeAccount$.pipe(map((account) => account?.email ?? "")),
  );

  readonly avatarUser = computed(() => ({
    name: this.displayName(),
    email: this.email(),
  }));

  readonly canChangeEmail = toSignal(
    this.accountService.activeAccount$.pipe(
      filter((account): account is Account => account != null),
      switchMap((account) => this.userDecryptionOptionsService.hasMasterPasswordById$(account.id)),
    ),
    { initialValue: false },
  );

  readonly managingOrganization = toSignal(
    this.accountService.activeAccount$.pipe(
      filter((account): account is Account => account != null),
      switchMap((account) => this.organizationService.organizations$(account.id)),
      map((organizations) => organizations.find((o) => o.userIsClaimedByOrganization === true)),
    ),
  );

  constructor(
    private readonly apiService: ApiService,
    private readonly accountService: AccountService,
    private readonly avatarService: AvatarService,
    private readonly dialogService: DialogService,
    private readonly organizationService: OrganizationService,
    private readonly keyService: KeyService,
    private readonly logService: LogService,
    private readonly userDecryptionOptionsService: UserDecryptionOptionsServiceAbstraction,
  ) {}

  async ngOnInit() {
    try {
      const profile = await this.apiService.getProfile();
      this.profile.set(profile);
      this.displayName.set(profile.name ?? "");
      await this.avatarService.setSyncAvatarImageUrl(profile.id, profile.avatarImageUrl ?? null);
      if (profile.avatarColor != null) {
        await this.avatarService.setSyncAvatarColor(profile.id, profile.avatarColor);
      }
    } catch (error) {
      this.logService.error("[ProfileComponent] Failed to load profile.", error as Error);
    } finally {
      this.loading.set(false);
    }

    void this.loadFingerprint();
  }

  private async loadFingerprint() {
    try {
      const userId = await firstValueFrom(
        this.accountService.activeAccount$.pipe(
          filter((account): account is Account => account != null),
          map((account) => account.id),
        ),
      );
      const publicKey = (await firstValueFrom(
        this.keyService.userPublicKey$(userId),
      )) as UserPublicKey;

      if (publicKey == null) {
        this.logService.error(
          "[ProfileComponent] No public key available for the user: " +
            userId +
            " fingerprint can't be displayed.",
        );
        return;
      }

      const generatedFingerprint = await this.keyService.getFingerprint(userId, publicKey);
      this.fingerprint.set(generatedFingerprint.join("-"));
    } catch (error) {
      this.logService.error("[ProfileComponent] Failed to load fingerprint.", error as Error);
    }
  }

  readonly openChangeAvatar = async () => {
    const profile = this.profile();
    if (profile == null) {
      return;
    }

    ChangeAvatarDialogComponent.open(this.dialogService, {
      data: { profile },
    });
  };

  readonly openEditName = async () => {
    const dialogRef = EditNameDialogComponent.open(this.dialogService, {
      name: this.displayName(),
    });
    const result = await lastValueFrom(dialogRef.closed);
    if (typeof result === "string") {
      this.displayName.set(result);
      const profile = this.profile();
      if (profile != null) {
        profile.name = result;
        this.profile.set(profile);
      }
    }
  };

  readonly openEditEmail = async () => {
    EditEmailDialogComponent.open(this.dialogService);
  };
}
